import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLibrary } from '../context/LibraryContext';
import { usePlayer } from '../context/PlayerContext';
import { FaPlay, FaPlus, FaEllipsisH, FaTrash, FaEdit, FaHeadphones } from 'react-icons/fa';
import '../styles/Playlists.css';

const Playlists = () => {
  const { playlists, createPlaylist, deletePlaylist, loading, error } = useLibrary();
  const { playPlaylist } = usePlayer();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({
    name: '',
    description: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPlaylists, setFilteredPlaylists] = useState([]);

  useEffect(() => {
    if (playlists.length > 0) {
      // Apply search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        setFilteredPlaylists(playlists.filter(playlist => 
          playlist.name.toLowerCase().includes(term) || 
          playlist.description.toLowerCase().includes(term)
        ));
      } else {
        setFilteredPlaylists(playlists);
      }
    }
  }, [playlists, searchTerm]);

  const handleCreatePlaylist = async (e) => {
    e.preventDefault();
    
    if (!newPlaylist.name.trim()) {
      return;
    }
    
    try {
      await createPlaylist(newPlaylist);
      setShowCreateModal(false);
      setNewPlaylist({
        name: '',
        description: ''
      });
    } catch (err) {
      console.error('Error creating playlist:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPlaylist({
      ...newPlaylist,
      [name]: value
    });
  };

  const handlePlayPlaylist = (playlist) => {
    if (playlist.songs && playlist.songs.length > 0) {
      playPlaylist(playlist);
    }
  };
  
  // Fonction pour ouvrir le DJ Mixer avec la playlist sélectionnée
  const openInDjMixer = (playlist, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (playlist.songs && playlist.songs.length > 0) {
      // Stockage temporaire de la playlist sélectionnée dans le localStorage
      localStorage.setItem('selectedPlaylistForDjMixer', JSON.stringify(playlist));
      navigate('/djmixer');
    }
  };

  const handleDeletePlaylist = async (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette playlist ?')) {
      try {
        await deletePlaylist(id);
      } catch (err) {
        console.error('Error deleting playlist:', err);
      }
    }
  };

  return (
    <div className="playlists-page">
      <div className="playlists-header">
        <h1>Mes Playlists</h1>
        <button 
          className="btn-create-playlist"
          onClick={() => setShowCreateModal(true)}
        >
          <FaPlus /> Nouvelle Playlist
        </button>
      </div>
      
      <div className="search-bar">
        <input
          type="text"
          placeholder="Rechercher une playlist..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {loading ? (
        <div className="loading">Chargement des playlists...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : filteredPlaylists.length === 0 ? (
        <div className="empty-playlists">
          <p>Aucune playlist trouvée.</p>
          {searchTerm && (
            <button 
              className="btn btn-secondary"
              onClick={() => setSearchTerm('')}
            >
              Effacer la recherche
            </button>
          )}
          {!searchTerm && playlists.length === 0 && (
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              Créer votre première playlist
            </button>
          )}
        </div>
      ) : (
        <div className="playlists-grid">
          {filteredPlaylists.map(playlist => (
            <Link 
              to={`/playlists/${playlist._id}`} 
              key={playlist._id} 
              className="playlist-card"
            >
              <div className="playlist-card-cover">
                <div className="playlist-card-count">
                  {playlist.songs.length} morceaux
                </div>
                <div className="playlist-card-actions">
                  <button 
                    className="btn-play-playlist"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePlayPlaylist(playlist);
                    }}
                    disabled={!playlist.songs || playlist.songs.length === 0}
                    title="Lire la playlist"
                  >
                    <FaPlay />
                  </button>
                  <button 
                    className="btn-dj-mixer"
                    onClick={(e) => openInDjMixer(playlist, e)}
                    disabled={!playlist.songs || playlist.songs.length === 0}
                    title="Ouvrir dans le DJ Mixer"
                  >
                    <FaHeadphones />
                  </button>
                </div>
              </div>
              <div className="playlist-card-info">
                <h3 className="playlist-card-title">{playlist.name}</h3>
                <p className="playlist-card-desc">{playlist.description}</p>
                <div className="playlist-card-footer">
                  <button 
                    className="btn-playlist-action"
                    onClick={(e) => handleDeletePlaylist(playlist._id, e)}
                    aria-label="Supprimer la playlist"
                  >
                    <FaTrash />
                  </button>
                  <button 
                    className="btn-playlist-action"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    aria-label="Modifier la playlist"
                  >
                    <FaEdit />
                  </button>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      
      {/* Create Playlist Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Créer une nouvelle playlist</h2>
              <button 
                className="btn-close"
                onClick={() => setShowCreateModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleCreatePlaylist} className="modal-form">
              <div className="form-group">
                <label htmlFor="name">Nom *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newPlaylist.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={newPlaylist.description}
                  onChange={handleInputChange}
                  rows="3"
                ></textarea>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowCreateModal(false)}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="btn-create"
                  disabled={!newPlaylist.name.trim()}
                >
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Playlists;
